import { useState } from "react";
import { X, User, Mail, MessageSquare, Send, CheckCircle, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ContactModal({ open, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  if (!open) return null;

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setError(""); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim())
      return setError("Please fill in all fields.");
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Something went wrong.");
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => { setForm({ name: "", email: "", message: "" }); setError(""); setSuccess(false); onClose(); };

  return (
    <div
      data-testid="contact-overlay"
      className="fixed inset-0 z-[90] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.75)", backdropFilter: "blur(8px)" }}
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        data-testid="contact-modal"
        className="relative w-full max-w-lg rounded-3xl overflow-hidden"
        style={{
          background: "linear-gradient(145deg, #111420 0%, #0C0E13 100%)",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 32px 64px rgba(0,0,0,0.6)",
        }}
      >
        {/* Top glow line */}
        <div className="h-px w-full" style={{ background: "linear-gradient(90deg, transparent, rgba(59,158,255,0.5) 40%, rgba(96,189,255,0.8) 50%, rgba(59,158,255,0.5) 60%, transparent)" }} />

        <button
          data-testid="contact-close"
          onClick={handleClose}
          className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors z-10"
        >
          <X size={14} className="text-white/50" />
        </button>

        <div className="px-8 pt-8 pb-8">
          {!success ? (
            <>
              {/* Header */}
              <div className="mb-7">
                <h2 className="font-outfit font-black text-white text-2xl sm:text-3xl tracking-tight mb-2">
                  Get in <span className="text-[#3B9EFF]">Touch</span>
                </h2>
                <p className="text-sm text-[#A1A1AA] font-inter leading-relaxed">
                  Have feedback, a partnership idea, or just want to say hello? We'd love to hear from you.
                </p>
              </div>

              {/* Quick contact strip */}
              <a
                href="mailto:contact@fanible.com"
                className="flex items-center gap-3 bg-white/[0.04] border border-white/8 rounded-xl px-4 py-3 mb-6 hover:border-[#3B9EFF]/30 transition-colors group"
              >
                <div className="w-8 h-8 rounded-lg bg-[#3B9EFF]/10 flex items-center justify-center flex-shrink-0">
                  <Mail size={14} className="text-[#3B9EFF]" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold">Direct Email</div>
                  <div className="text-sm text-white font-semibold">contact@fanible.com</div>
                </div>
                <ArrowRight size={14} className="text-[#A1A1AA] group-hover:text-[#3B9EFF] transition-colors" />
              </a>

              {/* Form */}
              <form onSubmit={submit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="relative">
                    <User size={13} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A1A1AA] pointer-events-none" />
                    <input
                      data-testid="contact-name"
                      type="text"
                      value={form.name}
                      onChange={set("name")}
                      placeholder="Your name"
                      className="w-full bg-white/[0.05] border border-white/8 rounded-xl pl-9 pr-3 py-3 text-white text-sm font-inter placeholder-[#555] outline-none focus:border-[#3B9EFF]/50 transition-all"
                    />
                  </div>
                  <div className="relative">
                    <Mail size={13} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#A1A1AA] pointer-events-none" />
                    <input
                      data-testid="contact-email"
                      type="email"
                      value={form.email}
                      onChange={set("email")}
                      placeholder="Your email"
                      className="w-full bg-white/[0.05] border border-white/8 rounded-xl pl-9 pr-3 py-3 text-white text-sm font-inter placeholder-[#555] outline-none focus:border-[#3B9EFF]/50 transition-all"
                    />
                  </div>
                </div>

                <div className="relative">
                  <MessageSquare size={13} className="absolute left-3.5 top-4 text-[#A1A1AA] pointer-events-none" />
                  <textarea
                    data-testid="contact-message"
                    value={form.message}
                    onChange={set("message")}
                    placeholder="Your message..."
                    rows={4}
                    className="w-full bg-white/[0.05] border border-white/8 rounded-xl pl-9 pr-3 py-3 text-white text-sm font-inter placeholder-[#555] outline-none focus:border-[#3B9EFF]/50 transition-all resize-none"
                  />
                </div>

                {error && <p data-testid="contact-error" className="text-red-400 text-xs font-inter px-1">{error}</p>}

                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  className="w-full flex items-center justify-center gap-2 bg-[#3B9EFF] text-black font-bold text-sm uppercase tracking-wider rounded-xl py-3.5 hover:bg-white transition-colors duration-150 disabled:opacity-50"
                >
                  {loading ? (
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  ) : (
                    <><Send size={14} /> Send Message</>
                  )}
                </button>
              </form>
            </>
          ) : (
            <div data-testid="contact-success" className="text-center py-6">
              <div className="w-16 h-16 rounded-full bg-[#3B9EFF]/10 border border-[#3B9EFF]/25 flex items-center justify-center mx-auto mb-5">
                <CheckCircle size={30} className="text-[#3B9EFF]" />
              </div>
              <h3 className="font-outfit font-black text-white text-xl mb-2">Message Sent!</h3>
              <p className="text-sm text-[#A1A1AA] font-inter leading-relaxed max-w-xs mx-auto">
                Thanks for reaching out. Our team will get back to you at <span className="text-white font-medium">{form.email}</span> within 24–48 hours.
              </p>
              <button onClick={handleClose} className="mt-6 text-xs text-[#3B9EFF] hover:text-white transition-colors font-semibold">
                Close
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
